import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import variposeManifest from '../data/varipose-manifest.json';
import ResponsiveVariposeImage from './ResponsiveVariposeImage';

export default function Varipose() {
  // All hooks at the very top
  const [scroll, setScroll] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);
  const animationRef = useRef();
  const scrollRef = useRef(0);
  const lastUpdateRef = useRef(performance.now());
  const [imagePaths] = useState(variposeManifest.map(f => `/img/varipose/${f}`));
  const [prevCenterIdx, setPrevCenterIdx] = useState(0);
  const centerImagesRef = useRef([]);

  useEffect(() => {
    function handleResize() {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let lastTime = performance.now();
    let speed = 0.1; // px per ms (30% faster)
    function animate(now) {
      const dt = now - lastTime;
      lastTime = now;
      scrollRef.current += speed * dt;
      // Only update React state at most every 16ms (about 60fps)
      if (now - lastUpdateRef.current > 16) {
        setScroll(scrollRef.current);
        lastUpdateRef.current = now;
      }
      animationRef.current = requestAnimationFrame(animate);
    }
    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  }, []);

  // Only after all hooks:
  const IMAGE_PATHS = imagePaths;
  const totalItems = IMAGE_PATHS.length;

  // Gallery logic
  const ITEM_WIDTH = 70;
  const GAP = 60;
  const SCALE_MAX = 3;
  const MAIN_IMG_HEIGHT = 'var(--main-img-height, 70vh)';
  const MAIN_IMG_WIDTH = `calc(${MAIN_IMG_HEIGHT} * 9 / 16)`;
  const THUMB_HEIGHT = Math.round(ITEM_WIDTH * 16 / 9);
  const VISIBLE_COUNT = 13;
  const totalWidth = totalItems * (ITEM_WIDTH + GAP);
  let offset = scroll % totalWidth;
  if (offset < 0) offset += totalWidth;
  const centerIdx = totalItems > 0 ? Math.round(offset / (ITEM_WIDTH + GAP)) % totalItems : 0;
  const trueCenterIdx = totalItems > 0 ? (centerIdx + totalItems) % totalItems : 0;

  // Crossfade logic
  useEffect(() => {
    if (totalItems === 0) return;
    const safePrevIdx = ((prevCenterIdx % totalItems) + totalItems) % totalItems;
    const safeTrueIdx = ((trueCenterIdx % totalItems) + totalItems) % totalItems;
    const images = centerImagesRef.current;
    if (!images[safeTrueIdx] || !images[safePrevIdx]) return;
    // Ensure only previous and current are visible, and set CSS transition
    images.forEach((img, i) => {
      if (!img) return;
      img.style.transition = 'opacity 0.33s';
      if (i === safeTrueIdx) {
        img.style.opacity = 0;
        img.style.zIndex = 2;
      } else if (i === safePrevIdx) {
        img.style.opacity = 1;
        img.style.zIndex = 1;
      } else {
        img.style.opacity = 0;
        img.style.zIndex = 1;
      }
    });
    // Force reflow
    images[safeTrueIdx].offsetHeight;
    // Animate new image in with CSS
    images[safeTrueIdx].style.opacity = 1;
    // After fade-in, hide previous image
    setTimeout(() => {
      if (images[safePrevIdx]) {
        images[safePrevIdx].style.opacity = 0;
        images[safePrevIdx].style.zIndex = 1;
      }
      setPrevCenterIdx(safeTrueIdx);
    }, 200);
  }, [trueCenterIdx, totalItems]);

  // Thumbnails
  const sideImages = [];
  const scrollOffset = offset / (ITEM_WIDTH + GAP);
  const visibleThumbs = VISIBLE_COUNT;
  const renderThumbs = totalItems * 2;
  for (let i = -renderThumbs / 2; i < renderThumbs / 2; i++) {
    if (IMAGE_PATHS.length === 0) continue;
    const imgIdx = (Math.floor(scrollOffset) + i + totalItems) % totalItems;
    const x = -(i - (scrollOffset - Math.floor(scrollOffset))) * (ITEM_WIDTH + GAP);
    if (Math.abs(x) > (ITEM_WIDTH + GAP) * (visibleThumbs / 2 + 1)) continue;
    const edgeFade = 2;
    const distFromCenter = Math.abs(i);
    let opacity = 1;
    if (distFromCenter > (visibleThumbs / 2 - edgeFade)) {
      const fadeDist = (visibleThumbs / 2) - distFromCenter;
      opacity = Math.max(0, 1 + fadeDist / edgeFade);
    }
    sideImages.push(
      <div
        key={`side-${i}-${imgIdx}`}
        style={{
          position: 'absolute',
          left: `calc(50% + ${x}px - ${ITEM_WIDTH / 2}px)`,
          top: '50%',
          width: ITEM_WIDTH,
          height: THUMB_HEIGHT,
          transform: `translateY(-50%) scale(1)`,
          transition: 'transform 0.15s',
          zIndex: 100 - Math.abs(i),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          opacity,
          pointerEvents: opacity === 0 ? 'none' : 'auto',
        }}
      >
        <ResponsiveVariposeImage
          src={IMAGE_PATHS[imgIdx]}
          alt={`Varipose ${imgIdx + 1}`}
          style={{ width: '100%', height: '100%', aspectRatio: '9/16', objectFit: 'cover', objectPosition: 'top', display: 'block', transition: 'opacity 0.3s', borderRadius: 'var(--border-radius)' }}
        />
      </div>
    );
  }

  // Render
  return (
    <div style={{ position: 'relative', width: '100%', overflowX: 'hidden', height: '100vh', minHeight: '100vh' }}>
      {/* Unified overlay for both desktop and mobile, styled with CSS */}
      <div className="varipose-overlay px-5">
        <div className="varipose-title">GENERATE NEW POSES</div>
        <div className="varipose-subtitle fs-7">Try new model poses on the fly from a single refernce image.</div>
        <Link to="/how-it-works" className="btn o-c00 c-c00 f-a fs-6 px-4 py-2 my-3">How it works</Link>
      </div>
      <style>{`
        .varipose-overlay {
          position: absolute;
          left: 0;
          top: 0;
          z-index: 200;
          pointer-events: none;
          background: var(--ab-c0);
          backdrop-filter: blur(20px);
          overflow: hidden;
          padding: 24px 32px 24px 32px;
          width: 50vw;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          height: 100%;
        }
        .varipose-title {
          font-family: var(--font-a);
          text-transform: uppercase;
          font-size: 32px;
          letter-spacing: 0.08em;
          margin: 0;
          text-align: left;
          color: var(--c00);
          line-height: 1;
        }
        .varipose-subtitle {
          font-family: var(--font-b);
          font-weight: 400;
          font-size: 18px;
          letter-spacing: 0.04em;
          text-align: left;
          margin-top: 8px;
          color: var(--c3);
          max-width: 30vw;
        }
        .varipose-main-img-container {
          --main-img-height: 70vh;
          height: 100%;
        }
        @media (max-width: 991px) {
          .varipose-overlay {
            position: static;
            max-width: 100vw;
            width: 100vw;
            height: auto;
            padding: 16px 12px 8px 12px;
            align-items: flex-start;
            background: var(--ab-c0);
            backdrop-filter: blur(6px);
          }
          .varipose-title {
            font-size: 24px;
            margin-bottom: 4px;
            width: 100vw;
            max-width: 100vw;
          }
          .varipose-subtitle {
            font-size: 15px;
            margin-bottom: 8px;
            width: 100vw;
            max-width: 100vw;
          }
          .varipose-main-img-container {
            --main-img-height: 60vh;
            height: auto;
          }
        }
      `}</style>
      <div
        className="varipose-main-img-container"
        ref={containerRef}
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          margin: '0 auto',
          touchAction: 'pan-y',
          userSelect: 'none',
          boxSizing: 'border-box',
        }}
      >
        {sideImages}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: MAIN_IMG_WIDTH,
            height: MAIN_IMG_HEIGHT,
            transform: `translate(-50%, -50%)`,
            zIndex: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            transition: 'transform 0.15s',
            background: 'transparent',
          }}
        >
          {IMAGE_PATHS.length === 0 ? (
            <div style={{color: 'red', fontWeight: 'bold'}}>No images loaded. (totalItems=0)</div>
          ) : (
            IMAGE_PATHS.map((src, i) => (
              <ResponsiveVariposeImage
                key={src}
                ref={el => centerImagesRef.current[i] = el}
                src={src}
                alt={`Varipose ${i + 1}`}
                style={{
                  width: MAIN_IMG_WIDTH,
                  height: MAIN_IMG_HEIGHT,
                  objectFit: 'cover',
                  objectPosition: 'top',
                  display: 'block',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  opacity: i === trueCenterIdx || i === prevCenterIdx ? 1 : 0,
                  zIndex: i === trueCenterIdx ? 2 : 1,
                  pointerEvents: 'none',
                  transition: 'none',
                  background: 'transparent',
                  borderRadius: 'var(--border-radius)',
                }}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
